/**
 * Moving someone in voice to another voice channel: Discord's Move Members
 * ("Move to…", and dragging a person onto a voice channel, which comes with
 * the redesign), and switching your own channel. The rules are the ones
 * the server checks (`MembersService.moveOrDisconnect`): Move Members in
 * the channel they are in, Move Members and Connect in the destination,
 * their own Connect not needed, no role hierarchy; yourself, a plain
 * switch with the join rules.
 */
import type { Snowflake } from '@neosocial/protocol';
import { api } from '../lib/api';
import { memberActions, moveTargets } from '../mod/perms';
import { joinVoice } from '../voice/controller';
import { useVoice } from '../voice/store';
import { useData } from './data';
import type { DataState } from './reducer';

/**
 * May `userId` be moved to voice channel `channelId` now (a drop target
 * lights up only then)? They are in voice in another channel, you may move
 * them (`memberActions`), and it is one of their `moveTargets`.
 */
export function canMoveMemberTo(
  d: DataState,
  guildId: Snowflake,
  userId: Snowflake,
  channelId: Snowflake,
): boolean {
  const from = d.voiceStates[guildId]?.[userId]?.channel_id ?? null;
  if (!from || from === channelId) return false;
  if (!memberActions(d, guildId, userId).move) return false;
  return moveTargets(d, guildId, userId).includes(channelId);
}

/**
 * How a move goes: `engine` (you, connected in this app: the voice engine
 * switches, as a click on the channel does), `server` (anyone else, or you
 * on another device: `PATCH /guilds/{id}/members/{user}` with `channel_id`)
 * or `none` (they are already there, or not in voice).
 */
export type MoveRoute = 'engine' | 'server' | 'none';

export function moveRoute(
  d: DataState,
  guildId: Snowflake,
  userId: Snowflake,
  channelId: Snowflake,
): MoveRoute {
  const from = d.voiceStates[guildId]?.[userId]?.channel_id ?? null;
  const v = useVoice.getState().session;
  const here =
    userId === d.me?.id &&
    v.guildId === guildId &&
    v.channelId !== null &&
    v.status !== 'idle' &&
    v.status !== 'failed';
  if (here) return v.channelId === channelId ? 'none' : 'engine';
  if (!from || from === channelId) return 'none';
  return 'server';
}

/**
 * Moves `userId` to voice channel `channelId` (`moveRoute`). Throws what the
 * route throws: `VoiceJoinError` from the engine (a password to type, a
 * full channel), an API error from the server (50013 without Move Members,
 * 100023 a channel ban on them, 400 not in voice). Returns the route taken.
 */
export async function moveMemberToChannel(
  guildId: Snowflake,
  userId: Snowflake,
  channelId: Snowflake,
): Promise<MoveRoute> {
  const route = moveRoute(useData.getState(), guildId, userId, channelId);
  if (route === 'engine') await joinVoice(channelId, { guildId });
  else if (route === 'server')
    await api.um.modifyMember(guildId, userId, { channel_id: channelId });
  return route;
}
